import { faCircleCheck } from "@fortawesome/pro-regular-svg-icons/faCircleCheck";
import { faCircleX } from "@fortawesome/pro-regular-svg-icons/faCircleX";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Avatar, Box, Button, CardContent, Grid, Stack, TextField, Typography } from "@mui/material";
import { AppGlobalClassKeys } from "app/appGlobalStyles";
import { MprcBoxDrawer, MprcCard, MprcClickable, MprcContainer, MprcLink, MprcRequestErrorDialog, MprcRequestErrorReport, MprcRequestIndicator } from "components";
import { IconCircleEllipsis } from "components/icons/IconCircleEllipsis";
import { MprcFeedTypography } from "components/MprcFeedTypography";
import { MprcSwitch } from "components/MprcSwitch";
import { OthersReportsResponse, PublicationActionResponse, PublicationType } from "generated/CoreApi_v1";
import { Formaters } from "lib/Formaters";
import { useCoreApi } from "providers/CoreApiProvider";
import { useModalUserProfile } from "providers/UserProfileProvider";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCardContext } from "../../CardBuilder";

///////////////////////////////////////////////////////////////////////////////////////

type OtherActionsDialogResult = {
    status: PublicationActionResponse;
    messageAuthor: string | null;
    messageRequester: string | null;
};


interface IOtherActionsProps {
    isComment: boolean;
    title: string | null;
    allowBlock: boolean;
    onClose: (result: OtherActionsDialogResult | undefined) => void;
}

export const OtherActionsDrawer: React.FC<IOtherActionsProps> = (props) => {
    const { title, isComment, allowBlock, onClose } = props;
    const { t } = useTranslation("knowledge");
    const [authorMessage, setAuthorMessage] = useState<boolean>(false);
    const [textAuthor, setTextAuthor] = useState<string | null>(null);
    const [requesterMessage, setRequesterMessage] = useState<boolean>(false);
    const [textRequester, setTextRequester] = useState<string | null>(null);
    const authorMessageRef = useRef<HTMLInputElement>(undefined);
    const requesterMessageRef = useRef<HTMLInputElement>(undefined);

    const doResult = (status: PublicationActionResponse) => () =>
        onClose(
            {
                status,
                messageAuthor: authorMessage ? textAuthor : null,
                messageRequester: requesterMessage ? textRequester : null
            }
        );

    return (
        <MprcBoxDrawer
            title={t("Feed.Card.ModerateReports.OtherActionsDrawer.Title")}
            subtitle={title}
            onClose={() => onClose(undefined)}
            content={
                <>
                    <MprcContainer>
                        <MprcSwitch
                            label={t("Feed.Card.ModerateReports.OtherActionsDrawer.EnableAuthorMessageText")}
                            checked={authorMessage}
                            noWrap={false}
                            onChange={(_, checked) => {
                                setAuthorMessage(checked);
                                setTimeout(() => authorMessageRef.current?.focus(), 50);
                            }}
                        />
                        {
                            authorMessage &&
                            <TextField
                                inputRef={authorMessageRef}
                                value={textAuthor}
                                multiline={true}
                                rows={5}
                                onChange={(e) => setTextAuthor(e.target.value)}
                                fullWidth
                                variant="outlined"
                                margin="normal"
                            />
                        }
                    </MprcContainer>

                    <MprcContainer>
                        <MprcSwitch
                            label={t("Feed.Card.ModerateReports.OtherActionsDrawer.EnableRequesterMessageText")}
                            checked={requesterMessage}
                            noWrap={false}
                            onChange={(_, checked) => {
                                setRequesterMessage(checked);
                                setTimeout(() => requesterMessageRef.current?.focus(), 50);
                            }}
                        />
                        {
                            requesterMessage &&
                            <TextField
                                inputRef={requesterMessageRef}
                                value={textRequester}
                                multiline={true}
                                rows={5}
                                onChange={(e) => setTextRequester(e.target.value)}
                                fullWidth
                                variant="outlined"
                                margin="normal"
                            />
                        }
                    </MprcContainer>

                    <Stack direction="column" spacing={2} justifyItems="center">
                        <Typography variant="h4" textAlign="center">
                            {t("Feed.Card.ModerateReports.OtherActionsDrawer.ActionTitle")}
                        </Typography>

                        {
                            !isComment
                                ? <>
                                    <Button
                                        variant="contained"
                                        color="success"
                                        onClick={doResult(PublicationActionResponse.ReportIgnore)}
                                    >
                                        {t("Feed.Card.ModerateReports.OtherActionsDrawer.IgnoreReportButton")}
                                    </Button>
                                    {allowBlock &&
                                        <>
                                            <Button
                                                variant="contained"
                                                color="muted"
                                                onClick={doResult(PublicationActionResponse.ReportPublicationObsolete)}
                                            >
                                                {t("Feed.Card.ModerateReports.OtherActionsDrawer.MarkObsoleteButton")}
                                            </Button>
                                            <Button
                                                variant="contained"
                                                color="warning"
                                                onClick={doResult(PublicationActionResponse.ReportBlockPublication)}
                                            >
                                                {t("Feed.Card.ModerateReports.OtherActionsDrawer.BlockButton")}
                                            </Button>
                                        </>
                                    }
                                    <Button
                                        variant="contained"
                                        color="error"
                                        onClick={doResult(PublicationActionResponse.ReportExcludePublication)}
                                    >
                                        {t("Feed.Card.ModerateReports.OtherActionsDrawer.DeleteButton")}
                                    </Button>
                                </>
                                : <>
                                    <Button
                                        variant="contained"
                                        color="success"
                                        onClick={doResult(PublicationActionResponse.ReportIgnore)}
                                    >
                                        {t("Feed.Card.ModerateReports.OtherActionsDrawer.IgnoreCommentButton")}
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="error"
                                        onClick={doResult(PublicationActionResponse.ReportExcludeComment)}
                                    >
                                        {t("Feed.Card.ModerateReports.OtherActionsDrawer.DeleteCommentButton")}
                                    </Button>
                                </>
                        }
                    </Stack>
                </>
            }
        />
    );
};

///////////////////////////////////////////////////////////////////////////////////////

interface IOtherReportsProps {
    actionId: string;
    title: string | null;
    onClose: () => void;
}

export const OtherReportsDrawer: React.FC<IOtherReportsProps> = (props) => {
    const api = useCoreApi();
    const { actionId, title, onClose } = props;
    const { t } = useTranslation("knowledge");
    const [loading, setLoading] = useState<boolean>(true);
    const [viewError, setViewError] = useState<any>(null);
    const [viewData, setViewData] = useState<OthersReportsResponse[]>([]);
    const { showUser } = useModalUserProfile();

    useEffect(() => {
        async function load() {
            try {
                var result = await api.v1.knowledgeManagement.getOtherReports({ actionId });
                setViewData(result.data);
            }
            catch (e) {
                setViewError(e);
            }
            finally {
                setLoading(false);
            }
        }

        load();
    }, []);

    return (
        <MprcBoxDrawer
            title={t("Feed.Card.ModerateReports.OtherReportsDrawer.Title")}
            subtitle={title}
            onClose={() => onClose()}
            requestIndicator={loading ? "request" : undefined}
            content={
                <>
                    {
                        viewError
                            ? <MprcRequestErrorReport error={viewError} />
                            : viewData.map((item, index) => (
                                <Stack key={index} direction="column" spacing={1}>
                                    <Grid container justifyContent="center" alignItems="center" spacing={1}>
                                        <Grid alignSelf="flex-start">
                                            <Avatar
                                                className={AppGlobalClassKeys.clickable}
                                                src={item.requester.thumbnailUrl}
                                                title={item.requester.name}
                                                onClick={() => showUser(item.requester.userId)}
                                            />
                                        </Grid>
                                        <Grid size="grow">
                                            <MprcFeedTypography variant="authorPrimary" title={item.requester.name}>
                                                {item.requester.name}
                                                <span>:</span>
                                            </MprcFeedTypography>
                                            <MprcFeedTypography variant="authorSecondary" title={item.requester.name} component="div">
                                                &nbsp;{t("Feed.Card.ModerateReports.ReportedText")} {Formaters.relativeTime(item.requestedOn)}
                                            </MprcFeedTypography>
                                            <MprcFeedTypography variant="authorCaption">
                                                &nbsp;{item.reasonName}
                                            </MprcFeedTypography>
                                            <MprcFeedTypography variant="authorSecondary" component="div">
                                                {item.message}
                                            </MprcFeedTypography>
                                        </Grid>
                                    </Grid>
                                </Stack>
                            ))
                    }
                </>
            }
        />
    );
};

///////////////////////////////////////////////////////////////////////////////////////

export const ModerateReportsFooter = () => {
    const {
        pub: { title, publicationType, moderateReport, interaction: { allowBlock } },
        onFeedItemChanged
    } = useCardContext();

    const api = useCoreApi();
    const { t } = useTranslation("knowledge");
    const { showUser } = useModalUserProfile();

    const [saving, setSaving] = useState<boolean>(false);
    const [error, setError] = useState<any>(null);
    const [dialog, setDialog] = useState<any>(null);

    const showOthersReports = () => {
        setDialog(<OtherReportsDrawer
            actionId={moderateReport!.actionId}
            title={title}
            onClose={() => setDialog(null)}
        />);
    };

    const execute = async (actionResponse: PublicationActionResponse, messageToAuthor: string | null = null, messageToRequester: string | null = null) => {
        setSaving(true);

        try {
            var result = await api.v1.knowledgeManagement.moderatePublication({ actionId: moderateReport!.actionId, actionResponse, messageToAuthor, messageToRequester });
            onFeedItemChanged({ moderateReport: { ...moderateReport!, analysed: result.data } });
        }
        catch (e) {
            setError(e);
        }
        finally {
            setSaving(false);
        }
    };

    const showOthersActions = () => {
        setDialog(
            <OtherActionsDrawer
                isComment={moderateReport!.comment != null}
                title={title}
                allowBlock={allowBlock}
                onClose={
                    (result) => {
                        setDialog(null);
                        if (result) execute(result.status, result.messageAuthor, result.messageRequester);
                    }
                }
            />
        );
    };


    return (
        <>
            {dialog}

            <MprcRequestIndicator type="post" visible={saving} />

            <MprcRequestErrorDialog error={error} onClose={() => setError(null)} />

            <MprcContainer>
                <MprcCard paperColor="notice">
                    <CardContent>
                        <Grid container justifyContent="center" alignItems="center" spacing={1}>
                            <Grid size="grow">
                                <MprcClickable onClick={() => showUser(moderateReport!.requester.userId)}>
                                    <MprcFeedTypography variant="authorPrimary" title={moderateReport!.requester.name}>
                                        {moderateReport!.requester.name}
                                        <span>:</span>
                                    </MprcFeedTypography>
                                </MprcClickable>
                                <MprcFeedTypography variant="authorSecondary" title={moderateReport!.requester.name}>
                                    &nbsp;{t("Feed.Card.ModerateReports.ReportedText")} {Formaters.relativeTime(moderateReport!.requestedOn)}
                                </MprcFeedTypography>
                                <MprcFeedTypography variant="authorCaption">
                                    &nbsp;{moderateReport!.reasonName}
                                </MprcFeedTypography>
                                <MprcFeedTypography variant="authorSecondary" component="div">
                                    {moderateReport!.message}
                                </MprcFeedTypography>
                            </Grid>
                            <Grid className={AppGlobalClassKeys.clickable}>
                                <Avatar
                                    src={moderateReport!.requester.thumbnailUrl}
                                    title={moderateReport!.requester.name}
                                    onClick={() => showUser(moderateReport!.requester.userId)}
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </MprcCard>

                {
                    moderateReport!.otherCount > 0 &&
                    <Box mt={1} width="100%" textAlign="right">
                        <MprcLink onClick={showOthersReports}>
                            <Typography variant="captionHeader" color="info.main">
                                {t("Feed.Card.ModerateReports.SeeOthersReports", { count: moderateReport!.otherCount })}
                            </Typography>
                        </MprcLink>
                    </Box>
                }
            </MprcContainer>

            <Grid container justifyContent="space-between" alignItems="center">
                {publicationType == PublicationType.Answer ?
                    <>
                        <Grid>
                            <Button variant="text" color="warning" onClick={() => execute(PublicationActionResponse.ReportExcludePublication)}>
                                {<FontAwesomeIcon icon={faCircleX} fixedWidth />}
                                <span>&nbsp;{t("Feed.Card.ModerateReports.ExcludeQuestionButtonText")}</span>
                            </Button>
                        </Grid>
                        <Grid>
                            <Button variant="text" color="success" onClick={() => execute(PublicationActionResponse.ReportIgnore)}>
                                {<FontAwesomeIcon icon={faCircleCheck} fixedWidth />}
                                <span>&nbsp;{t("Feed.Card.ModerateReports.KeepQuestionButtonText")}</span>
                            </Button>
                        </Grid>
                    </>
                    :
                    <>
                        <Grid>
                            <Button variant="text" color="error" onClick={() => execute(PublicationActionResponse.ReportBlockPublication)}>
                                {<FontAwesomeIcon icon={faCircleX} fixedWidth />}
                                <span>&nbsp;{t("Feed.Card.ModerateReports.BlockButtonText")}</span>
                            </Button>
                        </Grid>
                        <Grid>
                            <Button variant="text" color="success" onClick={() => execute(PublicationActionResponse.ReportIgnore)}>
                                {<FontAwesomeIcon icon={faCircleCheck} fixedWidth />}
                                <span>&nbsp;{t("Feed.Card.ModerateReports.KeepButtonText")}</span>
                            </Button>
                        </Grid>
                    </>
                }
                <Grid>
                    <Button variant="text" color="muted" onClick={showOthersActions}>
                        <IconCircleEllipsis fixedWidth />
                        <span>&nbsp;{t("Feed.Card.ModerateReports.OtherActionsButtonText")}</span>
                    </Button>
                </Grid>
            </Grid>
        </>
    );
};
